import { useCallback, useMemo } from 'react';

import { useProductDependencies } from '@atlassian/company-hub-common';
import {
	CoverPictureIdPropertyKey,
	isUnsplashImage,
	propertyToId,
} from '@atlassian/company-hub-common/media-components';
import {
	BLOGPOST_ATI,
	type EntityATI,
	PAGE_ATI,
	type SearchConfluencePartial,
	searchPageQuery,
	type SearchPageQueryVariables,
	SORT_FIELD_KEY,
	SORT_ORDER,
} from '@atlassian/dynamic-cards-search-client';
import {
	getConfluenceDateOptionFromCustomDates,
	getConfluenceDateOptionValues,
} from '@atlassian/dynamic-cards-search-page';

import { type Filters } from '../../types';
import { createNewCardWithDefaultParameters } from '../use-cards-extension-parameters-and-actions';

const getFilterValue = (filter?: string) => {
	return filter?.split(',') || [];
};

const getFilterDateRange = (filters: Filters) => {
	const { lastModified } = filters;
	if (lastModified && lastModified !== 'custom') {
		return [getConfluenceDateOptionValues(lastModified)];
	} else if (lastModified === 'custom') {
		if (filters?.from && filters?.to) {
			return [getConfluenceDateOptionFromCustomDates(filters.from, filters.to)];
		}
	}
	return [];
};

const getEntities = (type?: string) => {
	if (type === 'page') {
		return [PAGE_ATI];
	} else if (type === 'blogpost') {
		return [BLOGPOST_ATI];
	}
	return [PAGE_ATI, BLOGPOST_ATI];
};

const getConfluenceFilters = (filters: Filters) => {
	const { spaces, owners, labels, contributors, ancestor, verified } = filters;

	return {
		spacesFilter: getFilterValue(spaces),
		isVerified: verified || undefined,
		contributorsFilter: getFilterValue(contributors),
		ancestorIdsFilter: getFilterValue(ancestor),
		range: getFilterDateRange(filters),
		owners: getFilterValue(owners),
		labelsFilter: getFilterValue(labels),
	};
};

const getSearchPageQueryVariables = (
	cardCount: number,
	filters: Filters,
	cloudId: string,
	searchSessionId: string | null | undefined,
) => {
	const entities = getEntities(filters.type) as EntityATI[];

	return {
		query: '',
		cloudIdARI: `ari:cloud:confluence::site/${cloudId}`,
		tenantId: cloudId,
		limit: cardCount,
		entities,
		commonFilters: {},
		confluenceFilters: getConfluenceFilters(filters),
		jiraFilters: {
			issueFilter: {
				projectARIs: [],
				assigneeARIs: [],
			},
		},
		thirdPartyFilters: {
			titleMatchOnly: false,
			subtypes: [],
		},
		queryVersion: 1,
		searchSessionId: searchSessionId ?? '',
		sort: [
			{
				field: filters.sortField || SORT_FIELD_KEY.LAST_MODIFIED_DATE,
				order: filters.sortOrder || SORT_ORDER.DESC,
			},
		],
	};
};

export const useSearchPageResults = () => {
	const { cloudId, searchConfigProvider } = useProductDependencies();
	const { searchSessionId } = searchConfigProvider || {};

	const getDynamicCards = useCallback(
		async (cardCount: number, filters: Filters) => {
			const variables = getSearchPageQueryVariables(
				cardCount,
				filters,
				cloudId,
				searchSessionId,
			) satisfies SearchPageQueryVariables;

			const { data, errors } = await searchPageQuery({ variables });

			const results =
				data?.search.results.edges.map((edge) => edge.node as SearchConfluencePartial) || [];

			if (results.length === 0 || errors) {
				return { cards: [], errors };
			}

			const trimmedResults = results?.slice(0, Math.min(cardCount, results.length));

			const dynamicCards = trimmedResults?.map((result) => {
				const { title, description, url, id, confluenceEntity } = result;

				const newCard = createNewCardWithDefaultParameters();
				newCard.link = url;
				newCard.title = title;
				newCard.description = description;
				newCard.confluencePageId = id.split('/')[1];

				if (confluenceEntity) {
					const { subtype, properties } = confluenceEntity;

					const isLivePage = subtype === 'live';
					let imageProperty;

					if (isLivePage) {
						imageProperty = properties?.find(
							(image) => image?.key === CoverPictureIdPropertyKey.DRAFT,
						);
					} else {
						imageProperty = properties?.find(
							(image) => image?.key === CoverPictureIdPropertyKey.PUBLISHED,
						);
						if (!imageProperty) {
							imageProperty = properties?.find(
								(image) => image?.key === CoverPictureIdPropertyKey.DRAFT,
							);
						}
					}

					let imageSrc = propertyToId(imageProperty)?.value?.id;
					if (imageSrc && !isUnsplashImage(imageSrc)) {
						imageSrc = `${newCard.confluencePageId}/${imageSrc}`;
					}
					newCard.imageSrc = imageSrc;
				}

				return newCard;
			});

			return { cards: dynamicCards, errors };
		},
		[cloudId, searchSessionId],
	);

	return useMemo(() => ({ getDynamicCards }), [getDynamicCards]);
};
